import {
  type ChangeEvent,
  type ChangeEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { fixFalslyTranslatedMarkdown } from "senf-shared";
import { type ClassValue, type VariantProps, tv } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";

import { useMeasure } from "../../../hooks/useMeasure";
import CharacterCounter from "../../atoms/inputs/CharacterCounter";
import Button from "../button/Button";
import Icon from "../icon/Icon";
import type { IconName } from "../icon/names";
import Markdown from "../richTextEditor/Markdown";
import RichTextEditor from "../richTextEditor/RichTextEditor";

const InvisibleInputStyles = tv({
  slots: {
    wrapper: "relative flex w-full flex-col items-start justify-start",
    container: "flex w-full flex-row items-center ",
    iconContainer: "absolute z-10 -ml-1",
    text: "w-full select-text hyphens-auto whitespace-pre-wrap break-words py-2",
    input:
      "hover:ring-border-subdued placeholder:text-text-disabled focus:placeholder:text-text-placeholder focus:ring-border-highlight-default focus:ring- relative -mx-2 box-content w-full resize-none hyphens-auto whitespace-pre-wrap rounded-lg bg-transparent p-2 text-left !outline-none ring-1 ring-transparent",
  },

  variants: {
    textAlign: {
      left: {
        input: "text-start",
        text: "text-start",
      },
      center: {
        input: "text-center",
        text: "text-center",
      },
      right: {
        input: "text-right",
        text: "text-right",
      },
    },
    isError: {
      true: {
        input:
          "ring-border-critical-subdued hover:ring-border-critical-default ring-border",
      },
    },
    isFocus: {
      true: {
        input: "focus:ring-border-subdued focus:ring-1",
      },
    },
    hasIcon: {
      true: {
        text: "pl-6",
        input: "pl-6",
      },
    },
  },
});

export interface InvisibleInputProps
  extends Partial<HTMLTextAreaElement>,
    VariantProps<typeof InvisibleInputStyles> {
  size?:
    | "body-xs"
    | "body-xs-bold"
    | "body-sm"
    | "body-sm-bold"
    | "body-md"
    | "body-lg"
    | "body-md-bold"
    | "body-lg-bold"
    | "heading-sm"
    | "heading-md"
    | "heading-lg"
    | "heading-xl"
    | "heading-2xl"
    | "heading-3xl"
    | "heading-4xl"
    | "heading-5xl";
  leadingIcon?: IconName;
  type?: "number" | "input" | "textarea";
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onFocus?: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: ChangeEventHandler<HTMLTextAreaElement>;
  onClick?: ChangeEventHandler<HTMLTextAreaElement>;
  note?: string;
  error?: boolean;
  rows?: number;
  maxRows?: number;
  textAlign?: "left" | "center" | "right";
  mode?: "edit" | "display";
  markdown?: boolean;
  as?: "h1" | "h2" | "p";
  classNames?: Partial<
    Record<keyof (typeof InvisibleInputStyles)["slots"], ClassValue>
  >;
  defaultValue?: string;
  // classNames?: {
  //   wrapper?: string;
  //   container?: string;
  //   input?: string;
  // };
}

const getMinHeight = (
  ref: HTMLTextAreaElement | HTMLDivElement | null,
  rows: number,
) => {
  if (!ref) return 0;
  const lineHeight = Number.parseFloat(window.getComputedStyle(ref).lineHeight);
  return rows * lineHeight;
};

const adjustTextarea = (
  event: Partial<Event>,
  rows: number,
  maxRows?: number,
) => {
  if (!event?.target) return;
  const Element = event.target as HTMLTextAreaElement;
  Element.setAttribute("rows", rows.toString());
  const padding =
    Number.parseFloat(window.getComputedStyle(Element).paddingBlock) * 2;
  const lineHeight = Number.parseFloat(
    window.getComputedStyle(Element).lineHeight,
  );
  const scrollHeight = Element.scrollHeight - padding;
  const newRows = Math.round(scrollHeight / lineHeight);

  Element.setAttribute(
    "rows",
    Math.max(Math.min(newRows, maxRows || rows), rows).toString(),
  );
};

const InvisibleInput: React.FC<InvisibleInputProps> = ({
  id,
  defaultValue,
  leadingIcon,
  note,
  onBlur,
  maxRows,
  as,
  size = "body-md",
  textAlign = "left",
  rows = 1,
  mode = "edit",
  style,
  classNames,
  markdown,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const [{ ref }, { width }] = useMeasure<
    HTMLTextAreaElement | HTMLDivElement
  >();
  const mdxEditorRef = useRef<MDXEditorMethods>(null);
  const { t } = useTranslation();

  const value =
    props.value === null || props.value === undefined
      ? defaultValue
      : props.value;
  const showReset =
    mode === "edit" &&
    defaultValue &&
    props.value !== null &&
    props.value !== undefined &&
    isFocused;

  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleProgrammaticFocus = ({
    target,
  }: Partial<ChangeEvent<HTMLElement>>) => {
    setIsFocused(true);
    target?.focus();
  };
  const handleBlur = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const adjust = () => {
    if (!ref.current) return;
    adjustTextarea(
      { target: ref.current } as Partial<Event>,
      rows,
      maxRows || 5,
    );
  };

  useEffect(() => {
    window.addEventListener("resize", adjust);
    if (ref.current && "addEventListener" in ref.current) {
      ref.current.addEventListener("programmaticFocus", () =>
        handleProgrammaticFocus({
          target: ref.current,
        } as Partial<ChangeEvent<HTMLElement>>),
      );
    }
    return () => {
      window.removeEventListener("resize", adjust);
      ref.current?.removeEventListener("programmaticFocus", () =>
        handleProgrammaticFocus({
          target: ref.current,
        } as Partial<ChangeEvent<HTMLElement>>),
      );
    };
  }, []);

  useEffect(adjust, [value, maxRows, ref, width]);

  const { wrapper, container, iconContainer, input, text } =
    InvisibleInputStyles({
      textAlign,
      isError: props.error,
      isFocus: isFocused,
      hasIcon: !!leadingIcon,
    });

  if (mode === "display" && !value) return null;

  const DisplayModeTag =
    size === "body-md" || size === "body-sm" ? "p" : as || "h2";
  const typeProps =
    props.type === "number"
      ? { type: "tel", inputMode: "numeric", pattern: "[0-9]*" }
      : {};

  return (
    <div className={cn(wrapper({ class: classNames?.wrapper }))}>
      <div className={cn(container({ class: classNames?.container }))}>
        {leadingIcon && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
          <div
            className={iconContainer({
              class: classNames?.iconContainer,
            })}
            onClick={() =>
              ref.current?.dispatchEvent(new Event("programmaticFocus"))
            }
          >
            <Icon name={leadingIcon} />
          </div>
        )}

        {mode === "edit" ? (
          <>
            {markdown ? (
              <div
                className={input({
                  className: cn(classNames?.input, size, "cursor-text"),
                })}
                style={
                  {
                    minHeight: getMinHeight(ref.current, rows),
                    ...style,
                  } as React.CSSProperties
                }
                onClick={(e) => {
                  // mdxEditorRef?.current?.focus()
                  (e.currentTarget.querySelector('[contenteditable]') as HTMLElement)?.focus()
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    // mdxEditorRef?.current?.focus();
                    (e.currentTarget.querySelector('[contenteditable]') as HTMLElement)?.focus()
                  }
                }}
                ref={ref as React.RefObject<HTMLDivElement>}
              >
                <RichTextEditor
                  value={value}
                  ref={mdxEditorRef}
                  key={defaultValue}
                  placeholder={props.placeholder || ""}
                  isResetting={isResetting}
                  propSetIsFocused={setIsFocused}
                  size={size}
                  rows={rows}
                  onChange={(content) => {
                    if (props.onChange) {
                      props.onChange({
                        target: { value: content },
                      });
                    }
                  }}
                />
              </div>
            ) : props.type === "input" ? (
              <input
                id={id}
                ref={ref}
                onMouseDown={handleFocus}
                onBlur={(e: any) =>
                  handleBlur(e as ChangeEvent<HTMLTextAreaElement>)
                }
                {...(props as any)}
                {...typeProps}
                value={value || ""}
                style={style as React.CSSProperties}
                className={input({ className: cn(classNames?.input, size) })}
              />
            ) : (
              <textarea
                id={id}
                ref={ref}
                onMouseDown={handleFocus}
                onBlur={handleBlur}
                {...props}
                value={value}
                style={style as React.CSSProperties}
                className={input({ className: cn(classNames?.input, size) })}
                rows={1}
              />
            )}
          </>
        ) : (
          <>
            {markdown ? (
              <div
                className={text({ className: cn(classNames?.input, size) })}
                style={style as React.CSSProperties}
              >
                <Markdown
                  className={textAlign === "center" ? "centeredMarkdown" : ""}
                >
                  {fixFalslyTranslatedMarkdown(value)}
                </Markdown>
              </div>
            ) : (
              <DisplayModeTag
                className={text({ className: cn(classNames?.input, size) })}
                style={style as React.CSSProperties}
              >
                {value}
              </DisplayModeTag>
            )}
          </>
        )}
      </div>
      {(props.minLength || props.maxLength) && (
        <div className="absolute -bottom-[1.5rem] right-0 flex w-full flex-row items-center">
          <CharacterCounter
            value={value}
            minLength={props.minLength}
            maxLength={props.maxLength}
            note={note}
            error={props.error}
            className={`${
              isFocused || props.error ? "opacity-100" : "opacity-0"
            }`}
          />
        </div>
      )}
      {showReset && (
        <Button
          onMouseDown={() => {
            const value = { value: null };

            setIsResetting(true);
            setTimeout(() => {
              setIsResetting(false);
            }, 100);
            return props.onChange?.({
              target: value,
              currentTarget: value,
            } as any);
          }}
          leadingIcon="reload"
          leadingIconProps={{ className: "w-4 h-4" }}
          variant="shapeless"
          size="sm"
          classNameWrapper="absolute top-1/2 -translate-y-1/2 -right-10"
          tooltipLabel={t("global:reset")}
        />
      )}
    </div>
  );
};

export default InvisibleInput;
